import { useRef, useState } from "react";

export type FilterOption<TOptionValue extends string> = {
  label: string;
  value: TOptionValue;
  initialOpen?: boolean;
};

type AddFilterButtonProps<TOptionValue extends string> = {
  options: FilterOption<TOptionValue>[];
  onClose: (value?: FilterOption<TOptionValue>) => void;
  disabled?: boolean;
};

/**
 * AddFilterButton component - "+" button with dropdown menu for adding filters
 *
 * Adapted from operations-iol-webapp for Tailwind CSS styling.
 * Uses Material Symbols for icons.
 */
export function AddFilterButton<TOptionValue extends string>({
  options,
  onClose,
  disabled = false,
}: Readonly<AddFilterButtonProps<TOptionValue>>) {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen((prev) => !prev);
    }
  };

  const handleClose = (option?: FilterOption<TOptionValue>) => {
    setIsOpen(false);
    onClose(option);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Escape") {
      setIsOpen(false);
      buttonRef.current?.focus();
    }
  };

  // Close when clicking outside
  const handleBlur = (event: React.FocusEvent) => {
    // Check if the new focus target is outside the menu
    if (menuRef.current && !menuRef.current.contains(event.relatedTarget as Node)) {
      setIsOpen(false);
    }
  };

  return (
    <div className="relative" onBlur={handleBlur}>
      <button
        ref={buttonRef}
        id="add-filter-button"
        type="button"
        aria-controls={isOpen ? "add-filter-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={isOpen}
        disabled={disabled}
        onClick={handleToggle}
        className={`
          flex items-center gap-1.5 px-3 py-1.5 rounded-lg
          text-sm font-medium text-primary
          border border-primary/30 hover:bg-primary/10
          transition-colors duration-150
          disabled:opacity-50 disabled:cursor-not-allowed
        `}
        title="Add filter"
      >
        <span className="material-symbols-outlined text-lg">filter_list</span>
        <span>Add Filter</span>
      </button>

      {isOpen && (
        <div
          ref={menuRef}
          id="add-filter-menu"
          role="menu"
          aria-labelledby="add-filter-button"
          onKeyDown={handleKeyDown}
          className={`
            absolute left-0 top-full mt-1 z-50
            min-w-[200px] py-1
            bg-white dark:bg-[#192233] 
            border border-gray-200 dark:border-[#324467]
            rounded-lg shadow-lg
          `}
        >
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              role="menuitem"
              aria-label={`Add ${option.label} Filter`}
              onClick={() => handleClose(option)}
              className={`
                w-full px-4 py-2 text-left text-sm
                text-gray-700 dark:text-gray-200
                hover:bg-gray-100 dark:hover:bg-gray-700
                transition-colors duration-150
              `}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
